import { RESOURCES_NAMES } from "@/const/resource.constants"
import { useCreate, useDelete, useHttp, useUpdate } from "@/hooks/useHttp"
import { useUser } from "@/hooks/useUser"

import type {
  AIProviderInput,
  AIProviderResponse,
  SetAIProviderResponse,
  TenantConfigurationResponse,
  TestAIProviderResponse
} from "./useTenant.types"

export const useTenantSignup = <T = unknown, S = T>() => {
  const context = useCreate<T, S>(`${RESOURCES_NAMES.TENANTS.SIGNUP}`)

  return context
}

export const useGcpTokenValidation = <T = unknown, S = T>() => {
  const context = useCreate<T, S>(
    `${RESOURCES_NAMES.TENANTS.GCP_TOKEN_VALIDATION}`
  )

  return context
}

export const useGetTenantConfigurations = () => {
  const context = useHttp<TenantConfigurationResponse>(
    `${RESOURCES_NAMES.TENANTS.CONFIGURATIONS}`
  )

  return context
}

export const useUpdateTenantConfigurations = <T = unknown, S = T>() => {
  const { updateFeatureFlags } = useUser()

  // The chat panel mounts on the chatbotEnabled flag, which is otherwise read only at sign-in.
  const context = useUpdate<T, S>(
    `${RESOURCES_NAMES.TENANTS.CONFIGURATIONS}`,
    undefined,
    undefined,
    () => ({
      onSuccess: (_, payload) => {
        const { kai_chatbot } = (payload || {}) as { kai_chatbot?: boolean }

        if (kai_chatbot !== undefined)
          updateFeatureFlags({ chatbotEnabled: kai_chatbot })
      }
    })
  )

  return context
}

// The request carries the secret; drop the mutation from the cache once the modal closes.
const FORGET_SECRET = () => ({ gcTime: 0 })

export const useGetAIProvider = (enabled: boolean) => {
  const context = useHttp<AIProviderResponse>(
    `${RESOURCES_NAMES.TENANTS.AI_PROVIDER}`,
    undefined,
    // A 403 must not replace the Kai tab with the error page; the card shows the error.
    { enabled, throwOnError: false }
  )

  return context
}

export const useUpdateAIProvider = () => {
  const context = useUpdate<SetAIProviderResponse, AIProviderInput>(
    `${RESOURCES_NAMES.TENANTS.AI_PROVIDER}`,
    undefined,
    undefined,
    FORGET_SECRET
  )

  return context
}

export const useTestAIProvider = () => {
  const context = useCreate<TestAIProviderResponse, AIProviderInput>(
    `${RESOURCES_NAMES.TENANTS.AI_PROVIDER_TEST}`,
    undefined,
    undefined,
    FORGET_SECRET
  )

  return context
}

export const useDeleteAIProvider = () => {
  const context = useDelete<AIProviderResponse, void>(
    `${RESOURCES_NAMES.TENANTS.AI_PROVIDER}`
  )

  return context
}
